import { useEffect, useRef, useState } from 'react'
import alloyAnalytics from '@telus/alloy-analytics'
import {
  ButtonLink,
  Ribbon,
  PriceLockup,
  Box,
  FlexGrid,
  Card,
  Divider,
  Spacer,
  Callout,
  Typography,
  ChevronLink,
} from '@telus-uds/components-web'
import MemoIcon from '../MemoIcon/MemoIcon'
import {
  handleDefault,
  handleSelectDropdown,
  handlePriceToggle,
  handleProductName,
  handleProductPrice,
} from '../StickyCartV2/utils/cartUtils'
import ButtonLinkWrapper, { ButtonLinkWrapperProps } from '../ButtonLinkWrapper/ButtonLinkWrapper'
import { IPriceCart } from './StickyCartTypes'

import { LegalTextRenderer } from '../../../utils/text/TextRenderer'
import {
  StickyCartBlock,
  CartPrice,
  ProductContainer,
  ProductDescription,
  ProductTitle,
  ProductPrice,
  LinkButton,
  IconPosition,
  CartCallout,
  RibbonContainer,
} from './Styles'
import {
  IStickyCartProductDetails,
  IStickyCartPrice,
  IDefaultValues,
  IFiveYearValues,
  IThreeYearValues,
} from './StickyCartTypes'

export type CartProps = {
  stickyCartRibbon?: string
  stickyCartHeading?: string
  stickyCartSubHeading?: string
  stickyCartContract?: string
  bottomStickyCartContract?: string
  stickyCartCallout?: string
  stickyCartPriceSignDirection?: string
  priceToggle?: React.ReactNode | undefined
  priceToggleBlock?
  dropDownYearTermBlock?
  dropDownYearTerm?: React.ReactNode | undefined
  productDetails?: IStickyCartProductDetails[]
  stickyCartPrice?: IStickyCartPrice
  primaryButtonText?: string
  stickyCartSecondaryButtonText?: string
  stickyCartSecondaryButtonHref?: string
  alignStickySecondaryButtonText?: string
  cartSecondaryButton?: React.ReactNode
  cartSecondaryButtonFields?: boolean
  customButtonLinkCartProps?: ButtonLinkWrapperProps | undefined
  cartPrice: IPriceCart
  defaultValuesCart: IDefaultValues
  fiveYearValues: IFiveYearValues
  threeYearValues: IThreeYearValues
}

const Cart = ({
  stickyCartHeading,
  stickyCartSubHeading,
  stickyCartContract,
  bottomStickyCartContract,
  stickyCartCallout,
  productDetails,
  priceToggle,
  priceToggleBlock,
  dropDownYearTerm,
  dropDownYearTermBlock,
  primaryButtonText,
  stickyCartSecondaryButtonText,
  stickyCartSecondaryButtonHref,
  alignStickySecondaryButtonText = 'left',
  cartSecondaryButton,
  cartSecondaryButtonFields = false,
  customButtonLinkCartProps,
  cartPrice,
  defaultValuesCart,
  fiveYearValues,
  threeYearValues,
}: CartProps) => {
  const onOffState = useRef<boolean>(undefined)
  const yearTermCurrent = useRef<string>(undefined)
  const [yearTermValue, setYearTermValue] = useState<string>('')
  const [href, setHref] = useState<string>('')
  const [ribbonText, setRibbonText] = useState<string>('')
  const [active, setActive] = useState<boolean>(undefined)
  const [cartActive, setCartActive] = useState<boolean>(false)
  const [price, setPrice] = useState<string>(undefined)
  const [strikePrice, setStrikePrice] = useState<string>(undefined)

  useEffect(() => {
    const useMobilityCoupons = localStorage.getItem('useMobilityCoupons') === 'true' ? true : false
    const yearTerm = localStorage.getItem('yearTerm') === '5-year' ? '5-year' : '3-year'

    onOffState.current = useMobilityCoupons
    yearTermCurrent.current = yearTerm
    setActive(useMobilityCoupons)
    setYearTermValue(yearTerm)
    setCartActive(useMobilityCoupons)

    const defaultValues = handleDefault(
      'cart',
      yearTerm,
      priceToggleBlock,
      dropDownYearTermBlock,
      useMobilityCoupons,
      defaultValuesCart,
      fiveYearValues,
      threeYearValues,
      cartPrice
    )

    setHref(defaultValues.url)
    setRibbonText(defaultValues.ribbonText)
    setPrice(defaultValues.price)
    setStrikePrice(defaultValues.strikePrice)

    const onLocalChange = (event) => {
      const eventName = event.type
      const useMobilityCouponsLocal = localStorage.getItem('useMobilityCoupons') === 'true' ? true : false
      const yearTermLocal = localStorage.getItem('yearTerm') === '5-year' ? '5-year' : '3-year'

      if (eventName === 'selectDropdownEvent') {
        const result = handleSelectDropdown(
          'cart',
          yearTermLocal,
          useMobilityCouponsLocal,
          defaultValuesCart,
          fiveYearValues,
          threeYearValues,
          cartPrice
        )
        setYearTermValue(result.yearValue)
        setHref(result.url)
        setRibbonText(result.ribbonText)
        setPrice(result.price)
        setStrikePrice(result.strikePrice)
      } else {
        const result = handlePriceToggle(
          'cart',
          useMobilityCouponsLocal,
          yearTermLocal,
          defaultValuesCart,
          fiveYearValues,
          threeYearValues,
          cartPrice,
          dropDownYearTermBlock
        )
        setActive(result.offOnState)
        setCartActive(result.offOnState)
        setHref(result.url)
        setRibbonText(result.ribbonText)
        setPrice(result.price)
        setStrikePrice(result.strikePrice)

        // Analytics for price toggle event
        window.dataLayer.event = {
          name: 'unlock your telus discounts',
          action: result.offOnState ? 'on' : 'off',
          details: window.dataLayer.page.name,
        }
        alloyAnalytics
          .event('toggleClicks')
          .webInteraction({
            name: 'm_h_unlock_telus_discounts',
            toolSelections: result.offOnState ? 'm_h_unlock_telus_discounts:on' : 'm_h_unlock_telus_discounts:off',
          })
          .fire()
      }
    }

    window.addEventListener('selectDropdownEvent', onLocalChange)
    window.addEventListener('priceToggleEvent', onLocalChange)

    return () => {
      window.removeEventListener('selectDropdownEvent', onLocalChange)
      window.removeEventListener('priceToggleEvent', onLocalChange)
    }
  }, [])

  return (
    <StickyCartBlock>
      <Card
        variant={{ padding: 'custom' }}
        tokens={{
          paddingBottom: 20,
          paddingLeft: 20,
          paddingRight: 20,
          paddingTop: 20,
        }}
      >
        {ribbonText && (
          <RibbonContainer>
            <Ribbon left={-8} top={-12} wrap>
              <LegalTextRenderer>{ribbonText}</LegalTextRenderer>
            </Ribbon>
          </RibbonContainer>
        )}
        {stickyCartHeading && (
          <div style={{ paddingBottom: '16px' }}>
            <Typography block variant={{ size: 'h3', colour: 'dark' }} heading="h3">
              <LegalTextRenderer>{stickyCartHeading}</LegalTextRenderer>
            </Typography>
          </div>
        )}
        {stickyCartSubHeading && (
          <Typography block variant={{ size: 'h4' }} tokens={{ color: '#414547' }} heading="h3">
            <LegalTextRenderer>{stickyCartSubHeading}</LegalTextRenderer>
          </Typography>
        )}
        {price && (
          <CartPrice>
            <FlexGrid.Row verticalAlign="bottom" horizontalAlign="start">
              <PriceLockup
                price={price}
                size={cartPrice.stickyCartPriceSize}
                rateText={cartPrice.stickyCartRateText}
                signDirection={cartPrice.stickyCartPriceSignDirection}
              />
              {strikePrice && (
                <Box horizontal={2}>
                  <PriceLockup
                    price={strikePrice}
                    strikeThrough
                    size={cartPrice.strikePriceSize}
                    a11yText={`was 24 dollars per month`}
                    signDirection={cartPrice.strikePriceSignDirection}
                    rateText={cartPrice.strikePriceRateText}
                  />
                </Box>
              )}
            </FlexGrid.Row>
          </CartPrice>
        )}
        {stickyCartContract && (
          <Typography block tokens={{ fontWeight: 300 }}>
            <LegalTextRenderer>{stickyCartContract}</LegalTextRenderer>
          </Typography>
        )}
        {priceToggleBlock && dropDownYearTermBlock && (
          <div id="dropdownYearTermBlock">
            {dropDownYearTermBlock && (
              <>
                <Spacer space={2} /> {dropDownYearTerm}
              </>
            )}
          </div>
        )}
        {priceToggleBlock && (
          <div id="priceToggleBlock">
            <Spacer space={3} />
            {priceToggle}
            <Spacer space={3} />
          </div>
        )}
        <Spacer space={2} />
        <Divider />
        <Spacer space={2} />
        {bottomStickyCartContract && (
          <>
            <Typography block tokens={{ fontWeight: 300 }}>
              <LegalTextRenderer>{bottomStickyCartContract}</LegalTextRenderer>
            </Typography>
            <Spacer space={4} />
          </>
        )}
        {productDetails &&
          productDetails.map((product) => {
            return (
              <ProductContainer key={product.productId} data-testid={'stickyCartProduct-testid'}>
                <ProductDescription color={product.productColor}>
                  <IconPosition>
                    <MemoIcon icon={product.productIcon}></MemoIcon>
                  </IconPosition>
                  <ProductTitle>
                    <LegalTextRenderer>
                      {handleProductName(priceToggleBlock, dropDownYearTermBlock, active, yearTermValue, product)}
                    </LegalTextRenderer>
                  </ProductTitle>
                </ProductDescription>
                <ProductPrice>
                  {handleProductPrice(
                    priceToggleBlock,
                    dropDownYearTermBlock,
                    active,
                    yearTermValue,
                    product,
                    cartPrice
                  )}
                </ProductPrice>
              </ProductContainer>
            )
          })}
        {stickyCartCallout && (
          <div style={{ paddingBottom: '16px' }}>
            <Callout variant={{ rounded: true }}>
              <CartCallout>
                <LegalTextRenderer>{stickyCartCallout}</LegalTextRenderer>
              </CartCallout>
            </Callout>
          </div>
        )}
        <Box top={2} flex={1}>
          {customButtonLinkCartProps ? (
            <ButtonLinkWrapper
              {...customButtonLinkCartProps}
              buttonLinkProps={{
                ...customButtonLinkCartProps.buttonLinkProps,
                url: href ?? '',
              }}
            />
          ) : (
            <ButtonLink href={href} variant={{ priority: 'high', width: 'full' }}>
              {primaryButtonText}
            </ButtonLink>
          )}
          {cartSecondaryButtonFields ? (
            <LinkButton alignStickySecondaryButtonText={alignStickySecondaryButtonText} data-testid="link-button">
              {cartSecondaryButton}
            </LinkButton>
          ) : stickyCartSecondaryButtonText ? (
            <LinkButton alignStickySecondaryButtonText={alignStickySecondaryButtonText} data-testid="link-button">
              <ChevronLink href={stickyCartSecondaryButtonHref}>{stickyCartSecondaryButtonText}</ChevronLink>
            </LinkButton>
          ) : (
            ''
          )}
        </Box>
      </Card>
    </StickyCartBlock>
  )
}

export default Cart
